"use client"

import { useState, type ChangeEvent } from "react"
import clsx from "clsx"

import type { PropsWithClassName } from "~/@types/generics"
import type { BundleSelectsStates } from "~/components/ui/Product/ProductHeader/_data/serialize-bundle-selector"
import type { TProductHeaderBundle } from "~/components/ui/Product/ProductHeader/_data/serializer"
import Icon from "~/components/abstracts/Icon"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { objectEntries } from "~/utils/object-entries"

import * as css from "./styles.css"

type BundleOptionsSelectorProps = PropsWithClassName<{
  itemClassName?: string
  selectClassName?: string
  bundleOptions: TProductHeaderBundle["bundleOptions"]
  defaultValues: BundleSelectsStates
  onChange: (selectStates: BundleSelectsStates) => void
  outOfStockLabel?: string
}>

export function BundleOptionsSelector({
  className,
  defaultValues,
  itemClassName,
  selectClassName,
  bundleOptions,
  onChange,
  outOfStockLabel,
}: BundleOptionsSelectorProps) {
  const t = useTranslate()
  const [selectStates, setSelectStates] = useState(defaultValues)

  const onSelectChange = (e: ChangeEvent<HTMLSelectElement>, key: string) => {
    const updatedSelectStates = {
      ...selectStates,
      [key]: e.target.value,
    }

    setSelectStates(updatedSelectStates)
    onChange(updatedSelectStates)
  }

  const computedOutOfStockLabel = outOfStockLabel ?? t("product_bundle_variant_out_of_stock")

  return (
    <div
      className={clsx(className)}
      data-comp="UI/Product/ProductHeader/ProductDetails/BundleSelector/BundleOptionsSelector"
    >
      {bundleOptions &&
        objectEntries(bundleOptions)?.map(([key, { options, displayName }], index) => {
          const padStartedIndex = `${index + 1}`.padStart(2, "0")

          return (
            <div className={clsx(itemClassName, css.item)} key={key}>
              <span className={css.number}>{padStartedIndex}.</span>
              <span className={css.title}>
                {displayName}
                {options?.length === 1 && (
                  <span>
                    {options?.[0] && options?.[0]?.value?.length > 0 ? <>&nbsp;:&nbsp;</> : null}
                    <span>
                      {options?.[0]?.value} {options?.[0]?.disabled ? ` - ${computedOutOfStockLabel}` : ""}
                    </span>
                  </span>
                )}
              </span>
              <div className={css.SelectContainer}>
                {options && options?.length > 1 && (
                  <select
                    className={clsx(css.Select, selectClassName)}
                    name={key}
                    defaultValue={options?.find((option) => option?.value === selectStates[key])?.value}
                    onChange={(e) => {
                      onSelectChange(e, key)
                    }}
                  >
                    {options?.map((option) => {
                      return (
                        <option key={`${option?.value}-${index}`} value={option?.value ?? ""}>
                          {option?.value} {option?.disabled ? ` - ${computedOutOfStockLabel}` : ""}
                        </option>
                      )
                    })}
                  </select>
                )}
                {options && options?.length > 1 && (
                  <Icon className={css.ChevronDownIcon} name="ChevronDown" theme="blue-med" width={16} />
                )}
              </div>
            </div>
          )
        })}
    </div>
  )
}
